import RestaurantList from "../RestaurantList";
import type { MapPin, QueryResult, QuerySortField, Restaurant } from "@/API";
import type { ViewMode } from "../..";
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import Map from "../Map";

type Props = {
  isLoading: boolean;
  restaurants?: Restaurant[] | null;
  viewMode: ViewMode;
  totalRestaurant: number;
  isFetching: boolean;
  fetchRestaurant: (append?: boolean) => void;
  sortOption: QuerySortField | undefined;
  setSortOption: Dispatch<SetStateAction<QuerySortField | undefined>>;
  restaurantsError?: Error | null;
  data: QueryResult | null;
  pins: MapPin[] | null;
  setViewMode: Dispatch<SetStateAction<ViewMode>>;
  pinsIsloading: boolean;
};

export type DrawerState = "open" | "closed" | "half-open";

const SNAP_OPEN = "calc(100dvh - 140px)";
const SNAP_HALF = "calc(100dvh / 2)";
const SNAP_CLOSED = "140px";

const DrawerRestaurantsList = ({
  isLoading,
  data,
  viewMode,
  isFetching,
  fetchRestaurant,
  pins,
  pinsIsloading,
  setViewMode,
}: Props) => {
  const [drawerState, setDrawerState] = useState<DrawerState>("half-open");
  const [height, setHeight] = useState<string>(SNAP_HALF);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(0);
  const currentHeight = useRef(window.innerHeight / 2);

  useEffect(() => {
    if (drawerState === "open") {
      const h = window.innerHeight - 140;
      setHeight(SNAP_OPEN);
      currentHeight.current = h;
    }
    if (drawerState === "half-open") {
      const h = window.innerHeight / 2;
      setHeight(SNAP_HALF);
      currentHeight.current = h;
    }
    if (drawerState === "closed") {
      setHeight(SNAP_CLOSED);
      currentHeight.current = 140;
    }
  }, [drawerState]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    isDragging.current = true;
    startY.current = e.clientY;
    startHeight.current = currentHeight.current;
    e.currentTarget.setPointerCapture(e.pointerId); // clé du suivi fluide
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const delta = startY.current - e.clientY;
    const newHeight = Math.min(
      Math.max(startHeight.current + delta, 140),
      window.innerHeight - 140,
    );
    currentHeight.current = newHeight;
    setHeight(`${newHeight}px`);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const delta = startY.current - e.clientY;

    if (Math.abs(delta) < 10) return;

    if (delta > 0) {
      setDrawerState((prev) => (prev === "closed" ? "half-open" : "open"));
    } else {
      setDrawerState((prev) => (prev === "open" ? "half-open" : "closed"));
    }
  };

  return (
    <>
      <Map
        setViewMode={setViewMode}
        setDrawerState={setDrawerState}
        viewMode={viewMode}
        restaurants={pins ?? null}
        pinsIsloading={pinsIsloading}
        drawerState={drawerState}
      />

      <div
        className="fixed bottom-[0px] left-[0px] right-[0px] bg-[white]"
        style={{
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          border: "1px solid #E5E7EB",
          height: height,
          transition: isDragging.current ? "none" : "height 0.3s ease",
        }}
      >
        <div
          style={{
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
            touchAction: "none",
          }}
          className="flex justify-center h-[20px] bg-[white] py-[16px] cursor-grab active:cursor-grabbing"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div className="h-[5px] w-[36px] bg-[#CCCCCC] rounded-[12px]" />
        </div>

        <div>
          <RestaurantList
            pins={pins}
            drawerState={drawerState}
            fetchRestaurant={fetchRestaurant}
            isFetching={isFetching}
            viewMode={viewMode}
            isLoading={isLoading}
            totalRestaurant={data?.total ?? 0}
            restaurants={
              data?.data.filter((restaurant) => restaurant) as
                | Restaurant[]
                | undefined
                | null
            }
          />
        </div>
      </div>
    </>
  );
};

export default DrawerRestaurantsList;
